import {
  action,
  autorun,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'
import partition from 'lodash/partition'
import { isClient } from 'utils/runtimeUtils'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { extractErrorMessage } from 'utils/ApiUtils'
import { readOutsideReaction } from 'utils/mobx/readOutsideReaction'
import { logger } from 'services/logger'
import type {
  FaceDetectionResult,
  LocalPhoto,
  PetDetectionResult,
  Photo,
  RemotePhoto,
} from 'components/PhotoBank/PhotoBank.types'
import { type IPhotoBankUploadService } from './PhotoBankUploadService'
import type { PhotoBankStore } from './PhotoBankStore'
import { fetchPhotoPreviewUrls } from 'api/photoPreviews.api'
import {
  getPhotoKeyFromUrl,
  isPrivatePhotoUrl,
} from 'components/PhotoBank/PhotoBank.utils'

// A sent request can't be stopped, so each one is kept to about a second of analyzer work
// that the next face request may have to share the CPU with.
const PET_DETECTION_BATCH_SIZE = 5

const PET_DETECTION_INPUTS = {
  FILE: 'file 400',
  URL: 'url 250',
} as const

type PetDetectionInput =
  (typeof PET_DETECTION_INPUTS)[keyof typeof PET_DETECTION_INPUTS]

type PetDetectionPhoto = Pick<Photo, 'uid' | 'uploadSrc' | 'minimalResUrl'>

type DetectionThumb = Pick<Photo, 'url' | 'minimalResUrl'>

// The photo API downloads each image itself, and a private photo's preview link only works for its owner's browser,
// so private photos are sent as presigned URLs of their min previews. A photo whose preview is unavailable keeps its
// link, and the API reports that photo alone as failed.
async function getServerReadableThumbUrls(
  photos: DetectionThumb[]
): Promise<string[]> {
  const privateKeys = photos
    .filter(({ url }) => isPrivatePhotoUrl(url))
    .map(({ url }) => getPhotoKeyFromUrl(url))
  if (privateKeys.length === 0) {
    return photos.map(({ minimalResUrl }) => minimalResUrl)
  }

  const { previews } = await fetchPhotoPreviewUrls(privateKeys)
  return photos.map(({ url, minimalResUrl }) =>
    isPrivatePhotoUrl(url)
      ? previews[getPhotoKeyFromUrl(url)]?.min ?? minimalResUrl
      : minimalResUrl
  )
}

type DetectionOptions = {
  includeHappyData: boolean
  includePetData: boolean
  filterSmallFaces: boolean
}

export class PhotoDetector {
  private readonly photoBankStore: PhotoBankStore

  private readonly uploadService: IPhotoBankUploadService

  @observable private accessor petDetectionHoldCount = 0

  private isDetectingPets = false

  // In memory only, so a photo resumed after a reload has no blob and is sent by its thumbnail URL
  private readonly petDetectionBlobs = observable.map<string, Blob>(
    {},
    { deep: false }
  )

  constructor(
    photoBankStore: PhotoBankStore,
    uploadService: IPhotoBankUploadService
  ) {
    this.photoBankStore = photoBankStore
    this.uploadService = uploadService
    makeObservable(this)

    if (isClient()) {
      reaction(
        () =>
          this.petDetectionHoldCount === 0 &&
          this.getPhotosPendingPets().length > 0,
        (shouldDetectPets) => {
          if (shouldDetectPets) this.detectPendingPets()
        },
        { name: 'PhotoDetector:detectPendingPets' }
      )

      autorun(this.dropPetDetectionBlobsOfRemovedPhotos, {
        name: 'PhotoDetector:dropPetDetectionBlobsOfRemovedPhotos',
      })
    }
  }

  detectFromFiles = async (
    photos: LocalPhoto[],
    files: File[],
    petDetectionBlobs: Map<string, Blob>,
    { includeHappyData, includePetData, filterSmallFaces }: DetectionOptions
  ): Promise<void> => {
    if (includePetData) {
      runInAction(() => this.petDetectionBlobs.merge(petDetectionBlobs))
    }

    await this.detectFaces(photos, includePetData, () =>
      this.uploadService.detectFacesFromFiles(
        files,
        includeHappyData,
        filterSmallFaces
      )
    )
  }

  detectFromUrls = async (
    photos: RemotePhoto[],
    { includeHappyData, includePetData, filterSmallFaces }: DetectionOptions
  ): Promise<void> => {
    const thumbs = readOutsideReaction(() =>
      photos.map(({ url, minimalResUrl }) => ({ url, minimalResUrl }))
    )

    await this.detectFaces(photos, includePetData, async () => {
      let thumbUrls: string[]
      try {
        thumbUrls = await getServerReadableThumbUrls(thumbs)
      } catch (e) {
        logger.warning('Skipped URL face detection, preview links failed', {
          message: extractErrorMessage(e),
        })
        return []
      }

      return this.uploadService.detectFacesFromUrl(
        thumbUrls,
        includeHappyData,
        filterSmallFaces
      )
    })
  }

  // Pet and face models share the analyzer's CPU and faces gate the designer's crop, so pets wait
  // out each face request, and callers uploading a pick in batches hold them for the whole pick.
  holdPetDetectionDuring = async (work: () => Promise<void>): Promise<void> => {
    this.markPetDetectionHeld()
    try {
      await work()
    } finally {
      this.markPetDetectionReleased()
    }
  }

  private detectFaces = async (
    photos: Photo[],
    includePetData: boolean,
    fetchFaceDetectionResults: () => Promise<FaceDetectionResult[]>
  ): Promise<void> => {
    // Marked pending inside the hold, or the photos' pets would be sent ahead of their faces
    await this.holdPetDetectionDuring(async () => {
      if (includePetData) {
        this.markPetDetectionPending(photos)
      }

      const facesData = await fetchFaceDetectionResults()
      this.applyFaceDetectionData(photos, facesData)
    })
  }

  @action
  private markPetDetectionHeld = (): void => {
    this.petDetectionHoldCount++
  }

  @action
  private markPetDetectionReleased = (): void => {
    this.petDetectionHoldCount--
  }

  @action
  private applyFaceDetectionData = (
    photos: Photo[],
    facesData: FaceDetectionResult[]
  ): void => {
    photos.forEach((photo, index) => {
      const faceDetectionResult = facesData[index]
      photo.faces = faceDetectionResult?.faces ?? []
      photo.smallFacesFiltered = faceDetectionResult?.smallFacesFiltered ?? 0
      photo.faceDetectionSuccess = faceDetectionResult?.success ?? null
    })
  }

  @action
  private markPetDetectionPending = (photos: Photo[]): void => {
    photos.forEach((photo) => {
      photo.petDetection = { status: 'pending' }
    })
  }

  private getPhotosPendingPets = (): Photo[] =>
    this.photoBankStore.photos.filter(
      ({ petDetection }) => petDetection.status === 'pending'
    )

  private dropPetDetectionBlobsOfRemovedPhotos = (): void => {
    const removedPhotoUids = Array.from(this.petDetectionBlobs.keys()).filter(
      (uid) => !this.photoBankStore.getPhoto(uid)
    )
    runInAction(() => {
      removedPhotoUids.forEach((uid) => this.petDetectionBlobs.delete(uid))
    })
  }

  private detectPendingPets = async (): Promise<void> => {
    if (this.isDetectingPets) return

    this.isDetectingPets = true
    try {
      while (readOutsideReaction(() => this.petDetectionHoldCount === 0)) {
        const photosToDetect = readOutsideReaction(() =>
          this.getPhotosPendingPets()
            .slice(0, PET_DETECTION_BATCH_SIZE)
            .map(({ uid, uploadSrc, url, minimalResUrl }) => ({
              uid,
              uploadSrc,
              url,
              minimalResUrl,
              petDetectionBlob: this.petDetectionBlobs.get(uid),
            }))
        )
        if (photosToDetect.length === 0) return

        const [photosWithBlobs, photosWithoutBlobs] = partition(
          photosToDetect,
          ({ petDetectionBlob }) => petDetectionBlob
        )
        await Promise.all([
          this.detectPets(photosWithBlobs, PET_DETECTION_INPUTS.FILE, () =>
            this.uploadService.detectPetsFromFiles(
              photosWithBlobs.map(({ petDetectionBlob }) => petDetectionBlob!)
            )
          ),
          this.detectPets(
            photosWithoutBlobs,
            PET_DETECTION_INPUTS.URL,
            async () =>
              this.uploadService.detectPetsFromUrl(
                await getServerReadableThumbUrls(photosWithoutBlobs)
              )
          ),
        ])
      }
    } finally {
      this.isDetectingPets = false
    }
  }

  private detectPets = async (
    photos: PetDetectionPhoto[],
    petDetectionInput: PetDetectionInput,
    fetchPetDetectionResults: () => Promise<PetDetectionResult[]>
  ): Promise<void> => {
    if (photos.length === 0) return

    const petDetectionResults = await fetchPetDetectionResults().catch(
      (error: unknown) =>
        photos.map(() => ({
          success: false as const,
          error: extractErrorMessage(error),
        }))
    )
    this.applyPetDetectionResults(
      photos,
      petDetectionResults,
      petDetectionInput
    )
  }

  @action
  private applyPetDetectionResults = (
    photos: PetDetectionPhoto[],
    petDetectionResults: PetDetectionResult[],
    petDetectionInput: PetDetectionInput
  ): void => {
    photos.forEach(({ uid, uploadSrc }, index) => {
      this.petDetectionBlobs.delete(uid)
      const petDetectionResult = petDetectionResults[index]
      if (!petDetectionResult.success) {
        this.photoBankStore.updatePhoto(uid, (photo) => {
          photo.petDetection = { status: 'failed' }
        })
        analytics.track(EVENT_NAMES.PHOTO_PET_DETECTION_FINISHED, {
          'Upload Src': uploadSrc,
          'Pet Detection Input': petDetectionInput,
          'Pet Detection Success': false,
          Error: petDetectionResult.error,
        })
        return
      }

      const { pets } = petDetectionResult
      this.photoBankStore.updatePhoto(uid, (photo) => {
        photo.petDetection = { status: 'detected', pets }
      })
      analytics.track(EVENT_NAMES.PHOTO_PET_DETECTION_FINISHED, {
        'Upload Src': uploadSrc,
        'Pet Detection Input': petDetectionInput,
        'Pet Detection Success': true,
        'Pet Count': pets.length,
        'Pet Labels': pets.map((pet) => pet.label),
        'Pet Scores': pets.map((pet) => pet.score),
        'Pet Relative Sizes': pets.map((pet) => pet.width * pet.height),
      })
    })
  }
}
